import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Mail, MessageCircle, MessageSquare } from 'lucide-react';
import { Button, VStack } from '../../Shared/Components/ui';
import { useSendOtpMutation } from '../AuthApi';
import { OtpContact } from '../../Shared/types/auth';
import { toast } from 'react-toastify';
import { useInstance } from '../../Shared/Hooks/useInstance';
import AuthShell from '../Shared/AuthShell';
import AuthCard from '../Shared/AuthCard';
import AuthFooter from '../Shared/AuthFooter';

interface PassedData {
  username: string;
  password: string;
  sessionId: string;
  contacts: OtpContact[];
  from?: { pathname: string; search?: string };
}

const channelIcon = (channel: OtpContact['channel']) => {
  if (channel === 'email') return <Mail size={22} className="text-teal-primary" />;
  if (channel === 'whatsapp') return <MessageCircle size={22} className="text-teal-primary" />;
  return <MessageSquare size={22} className="text-teal-primary" />;
};

const channelLabel = (channel: OtpContact['channel']) => {
  if (channel === 'email') return 'Email';
  if (channel === 'whatsapp') return 'WhatsApp';
  return 'SMS';
};

const SelectContact = () => {
  const { instance } = useInstance();
  const navigate = useNavigate();
  const location = useLocation();
  const passedData = location.state as PassedData | null;

  const defaultContactId =
    passedData?.contacts.find(c => c.recommended)?.id ?? passedData?.contacts[0]?.id ?? '';
  const [selectedId, setSelectedId] = useState(defaultContactId);
  const [sendOtp, { isLoading }] = useSendOtpMutation();

  if (!passedData) {
    return <Navigate to="/login" replace />;
  }

  const handleSend = async () => {
    try {
      const response = await sendOtp({
        sessionId: passedData.sessionId,
        contactId: selectedId,
      }).unwrap();

      navigate('/otp', {
        state: {
          username: passedData.username,
          password: passedData.password,
          sessionId: passedData.sessionId,
          contactId: selectedId,
          destination: response.destination,
          from: passedData.from,
        },
      });
    } catch {
      toast.error('Failed to send code. Please try again.');
    }
  };

  return (
    <AuthShell
      side={{
        title: (
          <>
            Choose Where to <br />
            <span className="text-teal-accent">Receive Your Code</span>
          </>
        ),
        text: "Select a contact and we'll send your one-time verification code there.",
      }}
    >
      <AuthCard logo={instance?.logo}>
          <div className="text-center mb-8 pt-6">
            <h1 className="text-2xl tablet:text-3xl bricolage-grotesque-extrabold text-[#111827] mb-2">
              Send Code To
            </h1>
            <p className="text-slate-600 text-sm tablet:text-base manrope-medium">
              Select where you'd like to receive your verification code
            </p>
          </div>

          <VStack gap={3} className="mb-8">
            {passedData.contacts.map(contact => (
              <button
                key={contact.id}
                type="button"
                onClick={() => setSelectedId(contact.id)}
                className={[
                  'w-full flex items-center gap-3 rounded-2xl p-4 text-left transition-all',
                  selectedId === contact.id
                    ? 'ring-2 ring-teal-primary border border-teal-primary bg-teal-50/30'
                    : 'border border-slate-200 bg-white hover:border-teal-primary/50',
                ].join(' ')}
              >
                <span className="shrink-0">{channelIcon(contact.channel)}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[#111827] font-semibold text-sm truncate">
                    {contact.maskedValue}
                  </span>
                  <span className="block text-gray-secondary text-xs mt-0.5">
                    via {channelLabel(contact.channel)}
                  </span>
                </span>
                {contact.recommended && (
                  <span className="shrink-0 text-[10px] font-bold text-teal-primary bg-teal-50 border border-teal-primary/30 rounded-full px-2 py-0.5">
                    Recommended
                  </span>
                )}
              </button>
            ))}
          </VStack>

          <Button
            type="button"
            variant="primary"
            size="lg"
            className="w-full h-14 text-lg"
            isLoading={isLoading}
            disabled={!selectedId || isLoading}
            onClick={handleSend}
          >
            Send Code
          </Button>

          <AuthFooter displayName={instance?.displayName} divider />
      </AuthCard>
    </AuthShell>
  );
};

export default SelectContact;
